'use client';

import { t } from '@lingui/macro';
import { CircularProgress, TextField } from '@mui/material';
import { Autocomplete } from '@prismo-io/design-system';
import { useState } from 'react';
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import useDebounceValue from '../../../helpers/use-debounce';
import { useExperienceTypesAutocomplete } from './use-experience-types-autocomplete';

export type ContractTypesAutocompleteInputProps<
  F extends FieldValues = FieldValues,
> = {
  name: Path<F>;
  control: Control<F>;
  required?: boolean;
  label?: string;
  helperText?: string;
  disabled?: boolean;
};

export function ExperienceTypesAutocompleteInput<
  TFieldValues extends FieldValues,
>(props: ContractTypesAutocompleteInputProps<TFieldValues>) {
  const {
    name,
    control,
    required = false,
    label = undefined,
    helperText = undefined,
    disabled = false,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [inputTerm, setInputTerm] = useState<string | null>(null);

  const { data: options = [], isLoading } = useExperienceTypesAutocomplete({
    isOpen,
    term: inputTerm,
  });

  const isLoadingDebounced = useDebounceValue(isLoading, 400);

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
      }) => {
        return (
          <Autocomplete
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            value={value}
            loading={isLoadingDebounced}
            loadingText={t`Recherche des types de contrat`}
            noOptionsText={t`Aucun résultat`}
            disabled={disabled}
            options={options}
            filterOptions={(x) => x}
            getOptionLabel={(opt) => opt.name}
            onInputChange={(e, term) => {
              setInputTerm(term);
            }}
            onChange={(event, value, reason, details) => {
              onChange(value);
            }}
            onBlur={(event) => {
              onBlur();
            }}
            isOptionEqualToValue={(option, value) => {
              return value ? option.id === (value?.id ?? value) : false;
            }}
            renderInput={(params) => (
              <TextField
                label={label}
                {...params}
                error={!!error}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  required,
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                inputProps={{
                  ...params.inputProps,
                }}
                helperText={error ? error.message : helperText}
              />
            )}
          />
        );
      }}
    />
  );
}
